import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LogIn, LogOut, Clock, Loader2, Smile, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

const PHOTO_BASE = (() => {
  try {
    return new URL(API).origin;
  } catch {
    return '';
  }
})();

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * Parent self-service check-in/check-out page.
 *
 * Lives at `/parent/checkin`. Parents reach it by either:
 *   1. Scanning the printed QR code at the front desk (then logging in), or
 *   2. Tapping the "Sign In/Out" button on their parent dashboard.
 *
 * Shows each of the parent's children with a single big toggle button.
 */
const ParentCheckinPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  // Non-parents shouldn't see this page
  useEffect(() => {
    if (user && user.role !== 'parent') {
      toast.error('This page is for parents only');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent/checkin/children`, getAuthHeaders());
      setChildren(res.data.children || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load your children');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { if (user?.role === 'parent') load(); }, [user, load]);

  const toggle = async (child) => {
    setActingId(child.id);
    try {
      const url = child.is_checked_in
        ? `${API}/attendance/check-out`
        : `${API}/attendance/check-in`;
      await axios.post(
        url,
        { child_id: child.id, transport_type: 'parent_dropoff' },
        getAuthHeaders(),
      );
      toast.success(
        child.is_checked_in
          ? `${child.first_name} signed out — have a great day!`
          : `${child.first_name} signed in — see you later!`,
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sign-in failed — please ask staff for help');
    } finally {
      setActingId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Sign In / Out">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Sign In / Out">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="parent-checkin-page">
        <Card className="card-clay">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Smile className="w-5 h-5 text-primary" />
              Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Tap a child to sign them in or out. Your check-in time is recorded automatically.
            </p>
          </CardHeader>
        </Card>

        {children.length === 0 ? (
          <Card className="card-clay">
            <CardContent className="p-8 text-center text-muted-foreground">
              <p>No children linked to your account yet.</p>
              <p className="text-sm mt-1">Please ask the center director to link your child to your profile.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="parent-checkin-list">
            {children.map((c) => (
              <Card
                key={c.id}
                className={`card-clay border-l-4 ${c.is_checked_in ? 'border-l-green-500' : 'border-l-slate-300'}`}
                data-testid={`parent-child-row-${c.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  {c.photo_url ? (
                    <img
                      src={`${PHOTO_BASE}${c.photo_url}`}
                      alt={`${c.first_name}`}
                      className="w-16 h-16 rounded-full object-cover border"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center font-heading text-xl text-primary">
                      {c.first_name?.[0]}{c.last_name?.[0]}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-lg">
                      {c.first_name} {c.last_name}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {c.is_checked_in ? (
                        <>
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Signed in
                          </Badge>
                          {c.current_classroom && (
                            <span className="text-xs text-muted-foreground">
                              · {c.current_classroom}
                            </span>
                          )}
                          {c.check_in_time && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" /> since {formatTime(c.check_in_time)}
                            </span>
                          )}
                        </>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                          Not signed in
                        </Badge>
                      )}
                    </div>
                  </div>

                  <Button
                    size="lg"
                    onClick={() => toggle(c)}
                    disabled={actingId === c.id}
                    className={
                      c.is_checked_in
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }
                    data-testid={`parent-toggle-${c.id}`}
                  >
                    {actingId === c.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : c.is_checked_in ? (
                      <>
                        <LogOut className="w-5 h-5 mr-2" /> Sign Out
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5 mr-2" /> Sign In
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="text-xs text-center text-muted-foreground">
          Need help? Please ask the front-desk staff.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default ParentCheckinPage;
